import { Card } from '@/components/Card';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { articleAdminApi } from '../articleAdminApi';
import type { ArticleReviewSummaryProps } from '../articleReviewSummary';
import { Article } from '@/types/article';
import { adminApi } from './adminApi';
import { useAuth } from '@/contexts/AuthContext';
import Toast from '@/components/Toast';
import type { Category } from '@/types/category';
import type { Topic } from '@/types/topic';

export const ArticleReviewSummary: React.FC<ArticleReviewSummaryProps> = ({
  isSummaryReviewed,
  isExtractedWordsReviewed,
  isTranslationReviewed,
  isTtsReviewed,
  isPublishArticleTab,
}) => {
  const navigate = useNavigate();

  let summaryTitle = '';
  if (isSummaryReviewed) {
    summaryTitle = 'Review Summary';
  } else if (isExtractedWordsReviewed) {
    summaryTitle = 'Review Extracted Words';
  } else if (isTranslationReviewed) {
    summaryTitle = 'Review Translation';
  } else if (isTtsReviewed) {
    summaryTitle = 'Review TTS';
  } else if (isPublishArticleTab) {
    summaryTitle = 'Publish Articles';
  }

  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [groupedBy, setGroupedBy] = useState<'country' | 'topic'>(() => {
    const saved = localStorage.getItem('articleReviewGroupedBy');
    return saved === 'topic' ? 'topic' : 'country';
  });
  const [country, setCountry] = useState<string>(() => {
    // Initialize from localStorage or default to 'AU'
    const savedCountry = localStorage.getItem('articleReviewCountry');
    return savedCountry || 'AU';
  });
  const [reviewFilter, setReviewFilter] = useState<'all' | 'pending'>(() => {
    const savedFilter = localStorage.getItem('articleReviewFilter');
    return savedFilter === 'all' ? 'all' : 'pending';
  });
  const [, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>(() => {
    const savedCategory = localStorage.getItem('articleReviewCategory');
    return savedCategory || '';
  });
  const [topicFilter, setTopicFilter] = useState<string>(() => {
    const savedTopic = localStorage.getItem('articleReviewTopic');
    return savedTopic || '';
  });
  const [countries, setCountries] = useState<
    Array<{ countryCode: string; countryNameEN: string; countryNameVI: string }>
  >([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);

  // Persist country selection to localStorage
  useEffect(() => {
    localStorage.setItem('articleReviewCountry', country);
  }, [country]);

  // Persist review filter selection to localStorage
  useEffect(() => {
    localStorage.setItem('articleReviewFilter', reviewFilter);
  }, [reviewFilter]);

  // Persist groupedBy selection to localStorage
  useEffect(() => {
    localStorage.setItem('articleReviewGroupedBy', groupedBy);
  }, [groupedBy]);

  // Persist category selection to localStorage
  useEffect(() => {
    localStorage.setItem('articleReviewCategory', categoryFilter);
  }, [categoryFilter]);

  // Persist topic selection to localStorage
  useEffect(() => {
    localStorage.setItem('articleReviewTopic', topicFilter);
  }, [topicFilter]);

  // Fetch countries from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await adminApi.getAllCountries();
        setCountries(data);
      } catch (error) {
        console.error('Failed to load countries', error);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchCategoriesByCountry = async () => {
      if (groupedBy !== 'country' || !country) {
        setCategories([]);
        return;
      }
      try {
        const data = await adminApi.getAllCategoriesByCountry(country.toUpperCase());
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to load categories', error);
        setCategories([]);
      }
    };
    fetchCategoriesByCountry();
  }, [groupedBy, country]);

  useEffect(() => {
    const fetchTopics = async () => {
      if (groupedBy !== 'topic') {
        setTopics([]);
        return;
      }
      try {
        const data = await adminApi.getAllTopics();
        setTopics(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to load topics', error);
        setTopics([]);
      }
    };
    fetchTopics();
  }, [groupedBy]);

  const fetchArticles = async (
    append = false,
    filter = reviewFilter,
    countryParam = country,
    filterCode = groupedBy === 'country' ? categoryFilter : topicFilter
  ) => {
    if (groupedBy === 'country' && !countryParam) {
      return;
    }

    if (!filterCode) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await articleAdminApi.getAll({
        limit: 30,
        cursor: append ? (cursor ?? undefined) : undefined,
        country: groupedBy === 'country' ? countryParam : '',
        category: filterCode,
        orderBy: 'publishedAt',
        order: 'desc',
        isSummaryReviewPage: isSummaryReviewed,
        onlyPendingSummaryReview: isSummaryReviewed && filter === 'pending' ? true : false,
        onlyPendingExtractedWordsReview:
          isExtractedWordsReviewed && filter === 'pending' ? true : false,
        onlyPendingTranslationReview: isTranslationReviewed && filter === 'pending' ? true : false,
        onlyPendingTtsReview: isTtsReviewed && filter === 'pending' ? true : false,
        onlyPendingPublishReview: isPublishArticleTab && filter === 'pending' ? true : false,
      });
      // Expecting data shape: { items: ReviewArticle[], cursor?: string }
      const items: Article[] = data.items ?? data;
      setCursor(data.cursor ?? null);
      setArticles(prev => (append ? [...prev, ...items] : items));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const isPendingReview = (article: Article) => {
    return (
      (isSummaryReviewed && article.isSummaryReviewed !== true) ||
      (isExtractedWordsReviewed && article.isExtractedWordsReviewed !== true) ||
      (isTranslationReviewed && article.isTranslationReviewed !== true) ||
      (isTtsReviewed && article.isTtsReviewed !== true)
    );
  };

  /**
   * Check if article is published (only for publish tab)
   * @param article
   * @returns -1 if not published, 0 if published but publishedAt date > now, 1 if published and publishedAt date <= now
   */
  const isPublished = (article: Article) => {
    if (isPublishArticleTab) {
      if (!article.publishedAt) return -1;
      const publishedDate = new Date(article.publishedAt);
      const now = new Date();
      if (publishedDate > now) return 0;
      return 1;
    }
    return -1;
  };

  const pendingCount = useMemo(() => articles.filter(a => isPendingReview(a)).length, [articles]);

  const firstTwoSentences = (text?: string) => {
    if (!text) return '';
    const match = text.match(/[^.!?]*[.!?]/g);
    if (!match) return text.trim();
    const sentences = match.slice(0, 2).join(' ').trim();
    return sentences.endsWith('...') ? sentences : `${sentences}...`;
  };

  const [titleFilter, setTitleFilter] = useState('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'publishedAt' | 'title' | 'viewCount'>(
    'publishedAt'
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Sort articles before rendering
  const parseDateValue = (v: unknown) => (v ? new Date(String(v)).getTime() : 0);

  const sortedArticles = [...articles].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    if (sortBy === 'title') {
      aValue = String(aValue || '').toLowerCase();
      bValue = String(bValue || '').toLowerCase();
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    }
    if (sortBy === 'viewCount') {
      return sortOrder === 'asc'
        ? Number(aValue || 0) - Number(bValue || 0)
        : Number(bValue || 0) - Number(aValue || 0);
    }
    // For dates
    return sortOrder === 'asc'
      ? parseDateValue(aValue) - parseDateValue(bValue)
      : parseDateValue(bValue) - parseDateValue(aValue);
  });

  // Filter articles by title (case-insensitive)
  const filteredByTitleArticles = sortedArticles.filter(
    article =>
      article.title?.toLowerCase().includes(titleFilter.toLowerCase()) ||
      article.titleUS?.toLowerCase().includes(titleFilter.toLowerCase())
  );

  const { user } = useAuth();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    type?: 'success' | 'error' | 'info';
  } | null>(null);
  const notifTimeoutRef = React.useRef<number | null>(null);

  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'info' = 'info',
    duration = 3000
  ) => {
    setNotification({ message, type });
    if (notifTimeoutRef.current) window.clearTimeout(notifTimeoutRef.current);
    notifTimeoutRef.current = window.setTimeout(() => setNotification(null), duration);
  };

  useEffect(() => {
    return () => {
      if (notifTimeoutRef.current) window.clearTimeout(notifTimeoutRef.current);
    };
  }, []);

  const canSearch = groupedBy === 'country' ? Boolean(categoryFilter) : Boolean(topicFilter);

  const handleSearch = async () => {
    if (!canSearch) {
      showNotification(
        groupedBy === 'country'
          ? 'Please select a category before searching'
          : 'Please select a topic before searching',
        'error'
      );
      return;
    }

    setCursor(null);
    await fetchArticles(
      false,
      reviewFilter,
      country,
      groupedBy === 'country' ? categoryFilter : topicFilter
    );
  };

  // Reset selection when visible list or grouping/context changes
  useEffect(() => {
    setSelectedIds([]);
  }, [filteredByTitleArticles.length, groupedBy, country, reviewFilter]);

  useEffect(() => {
    setArticles([]);
    setCursor(null);
    setTitleFilter('');
  }, [groupedBy, country]);

  const toggleSelect = (e: React.SyntheticEvent, id?: string) => {
    e.stopPropagation();
    if (!id) return;
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const bulkApprove = () => {
    if (selectedIds.length === 0) return;
    setIsBulkSubmitting(true);
    const reviewer = user?.name || 'Unknown';

    // Optimistic UI update
    setArticles(prev =>
      prev.map(a =>
        a.articleId && selectedIds.includes(a.articleId)
          ? {
              ...a,
              isExtractedWordsReviewed: true,
              generateDictionary: true,
              extractedWordsReviewedBy: reviewer,
            }
          : a
      )
    );

    // Show non-blocking notification
    showNotification(`Bulk approve started for ${selectedIds.length} article(s)`, 'info');

    // Fire-and-forget API calls
    const countryParam = groupedBy === 'country' ? country : '';
    const toSend = selectedIds.slice();
    toSend.forEach(id => {
      articleAdminApi
        .update(countryParam, id, {
          isExtractedWordsReviewed: true,
          generateDictionary: true,
          extractedWordsReviewedBy: reviewer,
        })
        .catch(err => {
          console.error('Bulk approve failed for', id, err);
        });
    });

    // Clear selection immediately and allow navigation
    setSelectedIds([]);
    setTimeout(() => setIsBulkSubmitting(false), 800);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const onClickReview = (countryParam: string, articleId: string) => {
    // TODO: merge routes
    if (groupedBy === 'country' && countryParam && articleId) {
      countryParam = countryParam.toLowerCase();
      if (isSummaryReviewed) {
        navigate(`/editor/articles/summary/${countryParam}/${articleId}`);
      } else if (isExtractedWordsReviewed) {
        navigate(`/editor/articles/extracted-words/${countryParam}/${articleId}`);
      } else if (isTranslationReviewed) {
        navigate(`/editor/articles/translation/${countryParam}/${articleId}`);
      } else if (isTtsReviewed) {
        navigate(`/editor/articles/tts/${countryParam}/${articleId}`);
      } else if (isPublishArticleTab) {
        navigate(`/editor/articles/publish/${countryParam}/${articleId}`);
      }
    } else if (groupedBy === 'topic' && articleId) {
      if (isSummaryReviewed) {
        navigate(`/editor/articles/summary/${articleId}`);
      } else if (isExtractedWordsReviewed) {
        navigate(`/editor/articles/extracted-words/${articleId}`);
      } else if (isTranslationReviewed) {
        navigate(`/editor/articles/translation/${articleId}`);
      } else if (isTtsReviewed) {
        navigate(`/editor/articles/tts/${articleId}`);
      } else if (isPublishArticleTab) {
        navigate(`/editor/articles/publish/${articleId}`);
      }
    }
  };

  const getDetailHref = (article: Article) => {
    const id = article?.articleId;
    if (!id) return '#';
    if (groupedBy === 'country') {
      const c = country ? country.toLowerCase() : '';
      if (isSummaryReviewed) return `/editor/articles/summary/${c}/${id}`;
      if (isExtractedWordsReviewed) return `/editor/articles/extracted-words/${c}/${id}`;
      if (isTranslationReviewed) return `/editor/articles/translation/${c}/${id}`;
      if (isTtsReviewed) return `/editor/articles/tts/${c}/${id}`;
      if (isPublishArticleTab) return `/editor/articles/publish/${c}/${id}`;
      return `/editor/articles/${c}/${id}`;
    }
    if (isSummaryReviewed) return `/editor/articles/summary/${id}`;
    if (isExtractedWordsReviewed) return `/editor/articles/extracted-words/${id}`;
    if (isTranslationReviewed) return `/editor/articles/translation/${id}`;
    if (isTtsReviewed) return `/editor/articles/tts/${id}`;
    if (isPublishArticleTab) return `/editor/articles/publish/${id}`;
    return `/editor/articles/${id}`;
  };

  return (
    <>
      <Toast notification={notification} onClose={() => setNotification(null)} />
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{summaryTitle}</h3>

        <div className="flex items-center justify-between mb-4">
          {/* Grouped By */}
          <div className="col-span-2">
            <div className="flex items-center gap-4 mb-2">
              <label className="mr-2 font-medium text-sm text-gray-700">Grouped By</label>
              <div className="flex items-center gap-6">
                <label className="inline-flex items-center text-sm">
                  <input
                    type="radio"
                    name="groupedBy"
                    value="country"
                    checked={groupedBy === 'country'}
                    onChange={() => setGroupedBy('country')}
                    className="form-radio h-4 w-4 text-blue-600 border-gray-300"
                  />
                  <span className="ml-2">Country</span>
                </label>

                <label className="inline-flex items-center text-sm">
                  <input
                    type="radio"
                    name="groupedBy"
                    value="topic"
                    checked={groupedBy === 'topic'}
                    onChange={() => setGroupedBy('topic')}
                    className="form-radio h-4 w-4 text-blue-600 border-gray-300"
                  />
                  <span className="ml-2">Topic</span>
                </label>
              </div>
            </div>
          </div>

          {/* Show All or Pending only*/}
          <div>
            <label htmlFor="reviewFilter" className="mr-2 font-medium text-sm text-gray-700">
              Show:
            </label>
            <select
              id="reviewFilter"
              value={reviewFilter}
              onChange={e => setReviewFilter(e.target.value as 'all' | 'pending')}
              className="border rounded px-2 py-1 text-sm mr-2"
            >
              <option value="all">All Articles</option>
              <option value="pending">Articles need to be reviewed</option>
            </select>
          </div>
        </div>

        {/* Country Select */}
        {groupedBy === 'country' && (
          <div className="mb-4">
            <label htmlFor="country" className="mr-2 font-medium text-sm text-gray-700">
              Country <span className="text-red-500">*</span>
            </label>
            <select
              id="country"
              name="country"
              value={country}
              onChange={e => setCountry(e.target.value.toUpperCase())}
              className="border rounded px-2 py-1 text-sm mr-2"
              required
            >
              <option value="">Select a country</option>
              {countries.map(countryItem => (
                <option key={countryItem.countryCode} value={countryItem.countryCode}>
                  {countryItem.countryNameEN}
                </option>
              ))}
            </select>
          </div>
        )}

        {(groupedBy === 'country' || groupedBy === 'topic') && (
          <div className="mb-4 flex items-end justify-between gap-3">
            <div>
              {groupedBy === 'country' ? (
                <>
                  <label htmlFor="category" className="font-medium text-gray-700 mr-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="category"
                    value={categoryFilter}
                    onChange={e => setCategoryFilter(e.target.value)}
                    className="border rounded px-2 py-1 text-sm mr-2"
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category.categoryCode} value={category.categoryCode}>
                        {category.categoryNameEN}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <label htmlFor="topic" className="font-medium text-gray-700 mr-2">
                    Topic <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="topic"
                    value={topicFilter}
                    onChange={e => setTopicFilter(e.target.value)}
                    className="border rounded px-2 py-1 text-sm mr-2"
                  >
                    <option value="">Select a topic</option>
                    {topics.map(topic => (
                      <option key={topic.topicCode} value={topic.topicCode}>
                        {topic.topicNameEN}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>

            <button
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded p-4">
          <p className="text-sm text-blue-800">
            Total displaying Articles: <span className="font-bold">{articles.length}</span>
          </p>
          <p className="text-sm text-blue-800 mt-2">
            Pending Review: <span className="font-bold">{pendingCount}</span>
          </p>
        </div>

        <Card className="p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Articles List</h3>

          <div className="flex items-center justify-between mb-4">
            <div>
              <label htmlFor="titleFilter" className="font-medium text-gray-700 mr-2">
                Filter by title:
              </label>
              <input
                id="titleFilter"
                type="text"
                value={titleFilter}
                onChange={e => setTitleFilter(e.target.value)}
                className="border rounded px-2 py-1 text-sm mr-2"
                placeholder="Enter title..."
              />
              <span className="text-gray-600 ml-4">
                {filteredByTitleArticles.length} article(s) found
              </span>
            </div>

            <div>
              <label htmlFor="sortBy" className="mr-2 font-medium text-sm text-gray-700">
                Sort by:
              </label>
              <select
                id="sortBy"
                value={sortBy}
                onChange={e => setSortBy(e.target.value as typeof sortBy)}
                className="border rounded px-2 py-1 text-sm mr-2"
              >
                <option value="createdAt">Created At</option>
                <option value="publishedAt">Published At</option>
                <option value="title">Title</option>
                <option value="viewCount">Views</option>
              </select>
              <button
                className="text-sm px-2 py-1 border rounded"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>

          {/* Select and Bulk Approve Row (below Sort by) */}
          <div className="flex items-center justify-between mb-4 mt-3">
            <div>
              {isExtractedWordsReviewed && (
                <button
                  className="px-2 py-1 bg-blue-600 border rounded text-sm text-white hover:bg-blue-700"
                  onClick={e => {
                    e.stopPropagation();
                    const visibleIds = filteredByTitleArticles
                      .map(a => a.articleId)
                      .filter(Boolean) as string[];
                    const allSelected =
                      visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));
                    if (allSelected) {
                      setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
                    } else {
                      setSelectedIds(prev => Array.from(new Set([...prev, ...visibleIds])));
                    }
                  }}
                >
                  {filteredByTitleArticles.length > 0 &&
                  filteredByTitleArticles.every(
                    a => a.articleId && selectedIds.includes(a.articleId)
                  )
                    ? 'Clear ALL'
                    : 'Select All'}
                </button>
              )}
            </div>

            <div>
              {isExtractedWordsReviewed && (
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  onClick={e => {
                    e.stopPropagation();
                    bulkApprove();
                  }}
                  disabled={selectedIds.length === 0 || isBulkSubmitting}
                  title={
                    selectedIds.length === 0
                      ? 'Select articles first'
                      : `Approve ${selectedIds.length} article(s)`
                  }
                >
                  {isBulkSubmitting ? 'Approving...' : `Bulk Approve (${selectedIds.length})`}
                </button>
              )}
            </div>
          </div>

          {/* Article List */}
          <div className="space-y-4">
            {filteredByTitleArticles?.map(article => (
              <div
                key={article.articleId}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => onClickReview(country, article?.articleId)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClickReview(country, article?.articleId);
                  }
                }}
              >
                <div className="flex justify-between items-start">
                  <div className="mr-4 flex items-start" onClick={e => e.stopPropagation()}>
                    {isExtractedWordsReviewed ? (
                      <input
                        type="checkbox"
                        checked={Boolean(
                          article.articleId && selectedIds.includes(article.articleId)
                        )}
                        onChange={e => toggleSelect(e, article.articleId)}
                        onClick={e => e.stopPropagation()}
                        className="form-checkbox h-4 w-4 text-blue-600 border-gray-300 mt-2"
                        aria-label="Select article"
                      />
                    ) : null}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold text-gray-800">
                        <a
                          href={getDetailHref(article)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
                          }}
                          className="text-blue-600 hover:underline"
                        >
                          {article?.title || article?.titleUS}
                        </a>
                      </h4>
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {article?.category}
                      </span>
                      {!isPublishArticleTab && !isPendingReview(article) && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Reviewed
                        </span>
                      )}
                      {!isPublishArticleTab && isPendingReview(article) && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          Pending Review
                        </span>
                      )}
                      {isPublishArticleTab && (
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            isPublished(article) === 1
                              ? 'bg-green-100 text-green-800'
                              : isPublished(article) === 0
                                ? 'bg-blue-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {isPublished(article) === 1
                            ? 'Published'
                            : isPublished(article) === 0
                              ? 'Scheduled to be Published'
                              : 'Not Published'}
                        </span>
                      )}
                    </div>
                    {isPublishArticleTab && (
                      <div className="flex">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            article.isSummaryReviewed
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          Summary Review: {article.isSummaryReviewed ? 'Done' : 'Not yet'}
                        </span>
                        <span
                          className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                            article.isExtractedWordsReviewed
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          Extracted Words Review:{' '}
                          {article.isExtractedWordsReviewed ? 'Done' : 'Not yet'}
                        </span>
                        <span
                          className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                            article.isTranslationReviewed
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          Translation Review: {article.isTranslationReviewed ? 'Done' : 'Not yet'}
                        </span>
                        <span
                          className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                            article.isTtsReviewed
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          TTS Review: {article.isTtsReviewed ? 'Done' : 'Not yet'}
                        </span>
                      </div>
                    )}
                    <p className="text-gray-600 mb-2 text-sm">
                      {firstTwoSentences(article?.textALevelEN || article?.textALevelUS)}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Created at: {formatDate(article?.createdAt)}</span>
                      <span>•</span>
                      <span>Published at: {formatDate(article?.publishedAt)}</span>
                      <span>•</span>
                      <span>{article?.viewCount || 0} Views</span>
                    </div>
                  </div>
                  <button
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    onClick={() => onClickReview(country, article?.articleId)}
                  >
                    Review
                  </button>
                </div>
              </div>
            ))}
            {/* Load More Button */}
            {cursor && (
              <button
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                onClick={() =>
                  fetchArticles(
                    true,
                    reviewFilter,
                    country,
                    groupedBy === 'country' ? categoryFilter : topicFilter
                  )
                }
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            )}
          </div>
        </Card>
      </Card>
    </>
  );
};
